import {User} from "../../../../../api/accessControl/User.ts";
import {Link, useParams, useSearchParams} from 'react-router-dom';
import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/react";
import Modal from "../../../../../components/general/Modal.tsx";
import NarrowDrawer from "../../../../../components/general/NarrowDrawer.tsx";
import {useCallback, useEffect, useMemo, useState} from 'react';
import {BackendApi} from "../../../../../api/api.ts";
import {
    ArrowLeftIcon,
    ArrowRightEndOnRectangleIcon,
    ChevronRightIcon
} from "@heroicons/react/24/outline";
import {
    RiBankLine,
    RiBox3Line,
    RiDownloadLine,
    RiFileTextLine,
    RiGovernmentLine,
    RiGroupLine,
    RiLineChartLine,
    RiCalendarLine,
    RiMoneyEuroCircleLine,
    RiQuillPenLine,
    RiSettings3Line,
    RiTableLine
} from "react-icons/ri";
import {ChevronDownIcon} from "@heroicons/react/20/solid";
import {Tab, Tabs} from "../../../../../components/general/Tabs.tsx";
import ClientSettings from "./ClientSettings";
import BusinessAccessModal from "./BusinessAccessModal";
import CopyToClipboard from "../../../../../components/general/CopyToClipboard.tsx";

import Shell from "../../../../../components/specific/Shell.tsx";
import CashflowStatement from "../../../../../components/specific/CashflowStatement.tsx";
import {TaxHistoryView, TaxPayment} from "../../../../../components/specific/taxHistory";
import {Client} from "../../Client.ts";
import {
    PartnerClientCashBalanceHistoryChart
} from "./PartnerClientCashBalanceHistoryChart/PartnerClientCashBalanceHistoryChart.tsx";
import {VatReturnTab} from "./VatReturn/VatReturnTab.tsx";
import {useRefreshVatReturnDraftsOnDocumentChange, useVatReturns} from "./VatReturn/useVatReturns.ts";
import {useOpenLinkedVatReturnVersion} from "./VatReturn/useOpenLinkedVatReturnVersion.ts";
import {useVatReturnActions} from "./VatReturn/useVatReturnActions.ts";
import * as Sentry from "@sentry/react";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";
import type {BookingTarget} from "../../../../../components/specific/DrillDown/index.ts";
import TrialBalanceView from "../../../../../components/specific/trialBalance/index.ts";
import type {
    ExportedFile,
    PerMonthStatement,
    PeriodicBalanceSheetReport,
    PeriodicProfitAndLossReport,
    TrialBalanceReport,
} from "../../../../../components/specific/trialBalance/index.ts";
import {PerMonthView, isPerMonthStatement} from "../../../../../components/specific/trialBalance/index.ts";
import GeneralJournalBookingsTab from "./components/GeneralJournalBookings/GeneralJournalBookingsTab.tsx";
import {useGLAccounts} from "../../../../../hooks/useGLAccounts.ts";
import {useSubledgers} from "../../../../../hooks/useSubledgers.ts";
import {SubledgerProvider} from "../../../../../contexts/SubledgerContext.tsx";
import SubledgersSection from "./components/Subledgers/SubledgersSection.tsx";
import FixedAssetsTab from "./components/FixedAssets/FixedAssetsTab.tsx";
import {blobFromBase64} from "../../../../../utils/base64UrlConverters.ts";
import {logInAsSubordinate} from "../../../../../utils/OrgManagement.ts";
import {ENTREPRENEUR_CAN_SEE_REPORTS_SETTING} from "../../../../../lib/reportsAccess.ts";
import {DocumentImportSection} from "./components/ImportBookkeepingDocuments/DocumentImportSection.tsx";
import {ClientPayrollSection} from "./ClientPayrollSection.tsx";
import {getPayrollConnectionStatus} from "../../../../../lib/payrollConnect.ts";
import {useBKSDocumentImport} from "./components/ImportBookkeepingDocuments/useBKSDocumentImport.ts";
import type {Booking} from "./components/GeneralJournalBookings/GeneralJournalBookingsTab.model.ts";
import type {BackendAPITypes} from "../../../../../generated/blueprint.ts";
import type { BalanceSheetExportFormat, CurrencyEnum } from '../../../../../generated/enums.ts';

export interface ClientData extends Client {
    documentCollectionEmail: string;
}

const SETTINGS_NOTICE_KEY = 'pcfo_settings_moved_notice_dismissed';

function getBusinessInitials(name: string): string {
    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join('');
}

export default function PartnerClientSettingsPage({currentUser}: { currentUser: User }) {
    const {businessId} = useParams<{ businessId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const currentTab = searchParams.get('tab') ?? 'trial-balance';
    const setTab = (slug: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('tab', slug);
            return next;
        }, {replace: true});
    };
    const statementParam = searchParams.get('statement');
    const perMonthStatement: PerMonthStatement = isPerMonthStatement(statementParam)
        ? statementParam
        : 'profit-and-loss';
    const setPerMonthStatement = (statement: PerMonthStatement) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (statement === 'profit-and-loss') next.delete('statement');
            else next.set('statement', statement);
            return next;
        }, {replace: true});
    };
    const [clientData, setClientData] = useState<ClientData | null>(null);
    const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);
    const [isImportOpen, setIsImportOpen] = useState<boolean>(false);
    const [isPayrollOpen, setIsPayrollOpen] = useState<boolean>(() => searchParams.get('payroll') === 'open');
    const [isAccessOpen, setIsAccessOpen] = useState<boolean>(() => searchParams.get('access') === 'open');
    const [accessMemberCount, setAccessMemberCount] = useState<number | null>(null);
    const [settingsNoticeDismissed, setSettingsNoticeDismissed] = useState<boolean>(
        () => localStorage.getItem(SETTINGS_NOTICE_KEY) === 'true'
    );
    const dismissSettingsNotice = () => {
        localStorage.setItem(SETTINGS_NOTICE_KEY, 'true');
        setSettingsNoticeDismissed(true);
    };


    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [taxData, setTaxData] = useState<TaxPayment[]>([]);
    const {dispatch} = useNotifications();
    const token = currentUser.getToken();
    const api = useMemo(() => new BackendApi(token), [token]);
    const { glAccounts } = useGLAccounts(currentUser);
    const canUseSubledgers = currentUser.flags.getFlag('PARTNER__CAN_USE_SUBLEDGERS');
    const {
        subledgers,
        isLoading: isLoadingSubledgers,
        create: createSubledger,
        rename: renameSubledger,
    } = useSubledgers(currentUser, canUseSubledgers ? businessId ?? null : null);
    const canMakeManualBookings = currentUser.flags.getFlag('PARTNER_CAN_MAKE_MANUAL_BOOKINGS');
    const canImportBksDocuments = currentUser.flags.getFlag('PARTNER__CAN_IMPORT_BKS_DOCUMENTS');
    const canInviteEntrepreneurs = currentUser.flags.getFlag('PARTNER__CAN_INVITE_ENTREPRENEURS');
    const canSeePayroll = currentUser.flags.getFlag('PARTNER__CAN_SEE_PAYROLL');
    const bksImport = useBKSDocumentImport(api, businessId);
    const [payrollProviderName, setPayrollProviderName] = useState<string | null>(null);
    const vatReturns = useVatReturns(api, businessId, currentTab === 'vat');
    const clearLinkedVatSnapshot = useCallback(() => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('snapshot');
            return next;
        }, {replace: true});
    }, [setSearchParams]);
    useOpenLinkedVatReturnVersion(vatReturns, currentTab === 'vat', searchParams.get('snapshot'), clearLinkedVatSnapshot);
    useRefreshVatReturnDraftsOnDocumentChange(vatReturns.refreshDrafts);
    const vatReturnActions = useVatReturnActions(vatReturns);

    const reloadAccessCount = useCallback(async () => {
        if (!businessId || !canInviteEntrepreneurs) return;
        try {
            const accessList = await api.Partner__getBusinessAccessList(businessId);
            setAccessMemberCount(accessList.members.length);
        } catch (error) {
            console.error("Error loading access count:", error);
        }
    }, [api, businessId, canInviteEntrepreneurs]);

    useEffect(() => {
        reloadAccessCount();
    }, [reloadAccessCount]);

    useEffect(() => {
        if (!canSeePayroll) return;
        let cancelled = false;
        getPayrollConnectionStatus(api)
            .then(status => {
                if (!cancelled) setPayrollProviderName(status.name);
            })
            .catch(error => {
                console.error("Error loading payroll connection status:", error);
            });
        return () => { cancelled = true };
    }, [api, canSeePayroll]);

    useEffect(() => {
        if (searchParams.get('access') === 'open') {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete('access');
                return next;
            }, {replace: true});
        }
    }, [searchParams, setSearchParams]);

    const getAllSettingsForBusiness = useCallback(async (clientBusinessId: string) => {
        const [response, bookingSuggestions] = await Promise.all([
            api.Partner__business__settings(clientBusinessId),
            api.Partner__business__bookingSuggestions(clientBusinessId),
        ]);

        return {
            emailSeparationEnabled: response.settings['PARTNER_DOCUMENT_COLLECTION_EMAIL_SEPARATION_ENABLED'] === true,
            salesInvoiceEmailAddress: response.settings['PARTNER_DOCUMENT_COLLECTION_SALES_INVOICE_EMAIL_ADDRESS'] || null,
            purchaseInvoiceEmailAddress: response.settings['PARTNER_DOCUMENT_COLLECTION_PURCHASE_INVOICE_EMAIL_ADDRESS'] || null,
            otherDocumentsEmailAddress: response.settings['PARTNER_DOCUMENT_COLLECTION_OTHER_DOCUMENTS_EMAIL_ADDRESS'] || null,
            missingDocumentsStartDate: response.settings['ENTREPRENEUR_MISSING_DOCUMENTS_START_DATE'] || null,
            bookkeepingStartDate: response.settings['BOOKKEEPING_START_DATE'] || null,
            bookingSuggestions: bookingSuggestions.booking_suggestions,
            entrepreneurCanSeeReports: response.settings[ENTREPRENEUR_CAN_SEE_REPORTS_SETTING] === true,
        };
    }, [api]);

    if (!businessId) {
        return null;
    }

    useEffect(() => {
        async function loadData() {
            try {
                setIsLoading(true);
                const [clientsResponse, taxResponse] = await Promise.all([
                    api.Partner__myClients(new Date().getFullYear(), Math.floor(new Date().getMonth() / 3) + 1),
                    api.Business__tax__history(businessId).catch((error) => {
                        Sentry.captureException(error);
                        dispatch({
                            title: "Fout opgetreden",
                            description: "De belastinggeschiedenis kon niet worden geladen.",
                            type: "error",
                        });
                        return [];
                    }),
                ]);
                const client = clientsResponse.businesses.find(b => b.id === businessId);
                const docCollectionResponse = await api.Partner__entrepreneur__documentCollectionEmail(businessId!);
                if (client) {
                    setClientData({
                        ...client,
                        documentCollectionEmail: docCollectionResponse.email
                    } as ClientData);
                }

                setTaxData(taxResponse);

            } catch (error) {
                console.error("Error loading client data:", error);
            } finally {
                setIsLoading(false);
            }
        }

        loadData();
    }, [businessId]);


    if (isLoading) {
        return (
            <Shell currentUser={currentUser} isLoading={true}>
                <div className="p-4">Loading client data...</div>
            </Shell>
        );
    }

    if (!clientData) {
        return (
            <Shell currentUser={currentUser}>
                <div className="p-4">
                    <div className="mb-4">
                        <Link
                            to="/"
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors inline-block"
                        >
                            <ArrowLeftIcon className="h-5 w-5 text-gray-500 inline-block mr-2"/>
                            Back to dashboard
                        </Link>
                    </div>
                    <div className="bg-red-50 border-l-4 border-red-400 p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg"
                                     viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd"
                                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                          clipRule="evenodd"/>
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">
                                    Client not found or you don't have permission to view this client.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Shell>
        );
    }

    async function fetchTrialBalanceReport(
        startDate: string,
        endDate: string,
        currency?: CurrencyEnum,
        showPredictedAccounts: boolean = true,
        comparisonPeriod?: string,
        signal?: AbortSignal,
    ) {
        const response = await api.withAbortSignal(signal).Partner__getClientTrialBalanceReport(
            startDate,
            endDate,
            clientData.id,
            currency,
            comparisonPeriod,
            showPredictedAccounts,
        );
        return response as unknown as TrialBalanceReport;
    }

    async function refreshTrialBalanceCache() {
        return await api.Partner__refreshClientTrialBalanceCache({
            client_business_id: clientData.id,
        });
    }

    async function bookToGeneralLedgerAccount(
        generalLedgerAccountId: string,
        targets: BookingTarget[],
    ): Promise<void> {
        await api.Partner__bookToGeneralLedgerAccount({
            client_business_id: clientData.id,
            general_ledger_account_id: generalLedgerAccountId,
            targets,
        });
    }

    async function fetchProfitAndLossPerPeriod(
        startDate: string,
        endDate: string,
        currency?: CurrencyEnum,
        showPredictedAccounts: boolean = true,
        signal?: AbortSignal,
    ) {
        const response = await api.withAbortSignal(signal).Partner__getClientProfitAndLossPerPeriod(
            startDate,
            endDate,
            clientData.id,
            currency,
            showPredictedAccounts,
        );
        return response as unknown as PeriodicProfitAndLossReport;
    }

    async function fetchBalanceSheetPerPeriod(
        startDate: string,
        endDate: string,
        currency?: CurrencyEnum,
        showPredictedAccounts: boolean = true,
        signal?: AbortSignal,
    ) {
        const response = await api.withAbortSignal(signal).Partner__getClientBalanceSheetPerPeriod(
            startDate,
            endDate,
            clientData.id,
            currency,
            showPredictedAccounts,
        );
        return response as unknown as PeriodicBalanceSheetReport;
    }

    async function downloadProfitAndLossPdf(
        startDate: string,
        endDate: string,
        currency: CurrencyEnum,
        expanded: boolean,
        showPredictedAccounts: boolean
    ): Promise<Blob> {
        const file = await api.Partner__getClientProfitAndLossPdf(
            startDate,
            endDate,
            clientData.id,
            currency,
            expanded,
            showPredictedAccounts
        );
        return blobFromBase64(file.file_data, file.mimetype);
    }

    async function downloadBalanceSheet(
        startDate: string,
        endDate: string,
        currency: CurrencyEnum,
        exportFormat: BalanceSheetExportFormat,
        showPredictedAccounts: boolean,
        comparisonPeriod: string | undefined,
    ): Promise<ExportedFile> {
        const file = await api.Partner__exportClientBalanceSheet(
            startDate,
            endDate,
            clientData.id,
            exportFormat,
            currency,
            comparisonPeriod ?? null,
            showPredictedAccounts,
        );
        return {blob: blobFromBase64(file.file_data, file.mimetype), fileName: file.file_name};
    }

    async function fetchManualBookings(clientBusinessId: string): Promise<Booking[]> {
        const response = await api.Partner__getClientManualBookings(clientBusinessId, null, null);
        return response as Booking[];
    }

    async function saveManualBooking(
        payload: BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerMakeManualBookingPostRequestBody
    ): Promise<void> {
        await api.Partner__makeManualBooking(payload);
    }

    async function editManualBooking(
        payload: BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerEditManualBookingPostRequestBody
    ): Promise<void> {
        await api.Partner__editManualBooking(payload);
    }

    async function deleteManualBooking(
        payload: BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerDeleteManualBookingPostRequestBody
    ): Promise<void> {
        await api.Partner__deleteManualBooking(payload);
    }

    async function fetchBankAccounts(clientBusinessId: string) {
        return await api.Partner__entrepreneur__getBankAccounts(clientBusinessId);
    }

    async function fetchBankBalanceAtDate(
        clientBusinessId: string,
        bankAccountId: string,
        date: string
    ): Promise<{ balance: number | null; iban: string; institution_name: string }> {
        return await api.Partner__getBankAccountBalanceAtDate({
            client_business_id: clientBusinessId,
            bank_account_id: bankAccountId,
            target_date: date,
        });
    }

    return (
            <Shell currentUser={currentUser} title={
                <nav className="flex items-center gap-2 text-sm text-gray-500">
                    <Link to="/" className="transition-colors hover:text-gray-700">
                        Dashboard
                    </Link>
                    <ChevronRightIcon className="h-4 w-4 text-gray-300"/>
                    <span className="text-gray-500">{clientData.name}</span>
                </nav>
            }>

                <div className="flex items-start justify-between mb-8">
                    <div className="flex items-center">
                        <div
                            aria-hidden="true"
                            className="mr-4 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-superliteprimary text-lg font-semibold text-primary">
                            {getBusinessInitials(clientData.name)}
                        </div>
                        <div>
                            <h1 className="text-2xl font-semibold text-gray-900">{clientData.name}</h1>
                            <p className="text-sm text-gray-500 mt-1">
                                {clientData.fte_amount} {clientData.fte_amount === 1 ? 'medewerker' : 'medewerkers'}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                    {canInviteEntrepreneurs && (
                        <button
                            type="button"
                            onClick={() => setIsAccessOpen(true)}
                            title="Toegang beheren"
                            aria-label="Toegang beheren"
                            className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                        >
                            <RiGroupLine className="h-5 w-5 text-gray-400"/>
                            {accessMemberCount ?? '–'}
                        </button>
                    )}
                    <Menu as="div" className="relative inline-block text-left">
                        <MenuButton
                            className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
                            <RiSettings3Line className="h-5 w-5 text-gray-400"/>
                            Beheer
                            <ChevronDownIcon className="h-4 w-4 text-gray-400"/>
                        </MenuButton>
                        <MenuItems
                            transition
                            className="absolute right-0 z-40 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                        >
                            <div className="py-1">
                                <MenuItem>
                                    <button
                                        type="button"
                                        onClick={() => setIsSettingsOpen(true)}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100"
                                    >
                                        <RiSettings3Line className="h-5 w-5 text-gray-400"/>
                                        Instellingen
                                    </button>
                                </MenuItem>
                                {canInviteEntrepreneurs && (
                                    <MenuItem>
                                        <button
                                            type="button"
                                            onClick={() => setIsAccessOpen(true)}
                                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100"
                                        >
                                            <RiGroupLine className="h-5 w-5 text-gray-400"/>
                                            Toegang
                                        </button>
                                    </MenuItem>
                                )}
                                {canImportBksDocuments && (
                                    <MenuItem>
                                        <button
                                            type="button"
                                            onClick={() => setIsImportOpen(true)}
                                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100"
                                        >
                                            <RiDownloadLine className="h-5 w-5 text-gray-400"/>
                                            Importeren
                                        </button>
                                    </MenuItem>
                                )}
                                {canSeePayroll && (
                                    <MenuItem>
                                        <button
                                            type="button"
                                            onClick={() => setIsPayrollOpen(true)}
                                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100"
                                        >
                                            <RiMoneyEuroCircleLine className="h-5 w-5 text-gray-400"/>
                                            Payroll
                                        </button>
                                    </MenuItem>
                                )}
                                <MenuItem>
                                    <button
                                        type="button"
                                        onClick={() => logInAsSubordinate(clientData.id)}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100"
                                    >
                                        <ArrowRightEndOnRectangleIcon className="h-5 w-5 text-gray-400"/>
                                        Inloggen als klant
                                    </button>
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </Menu>
                    </div>
                </div>

            <div>
                <Tabs
                    activeSlug={currentTab}
                    onTabChange={setTab}
                    notice={settingsNoticeDismissed ? null : (
                        <div className="mt-4 mb-4 flex items-center gap-1 text-xs text-gray-400">
                            <RiSettings3Line className="h-3.5 w-3.5 flex-shrink-0"/>
                            <span className="leading-none">
                                Instellingen staan nu onder ‘Beheer’, rechtsboven.{' '}
                                <button
                                    type="button"
                                    onClick={dismissSettingsNotice}
                                    className="font-medium text-gray-500 underline underline-offset-2 hover:text-gray-700"
                                >
                                    Verbergen
                                </button>
                            </span>
                        </div>
                    )}
                >
                    <Tab label={'Kolommenbalans'} icon={RiTableLine} slug="trial-balance">
                        <TrialBalanceView
                            currentUser={currentUser}
                            fetchTrialBalanceReport={fetchTrialBalanceReport}
                            refreshTrialBalanceCache={refreshTrialBalanceCache}
                            bookToGeneralLedgerAccount={bookToGeneralLedgerAccount}
                            businessId={clientData.id}
                            legalEntityType={clientData.legal_entity_type}
                            downloadProfitAndLossPdf={downloadProfitAndLossPdf}
                            canExportProfitAndLossPdf={currentUser.flags.getFlag(
                                "PARTNER__CAN_EXPORT_PROFIT_AND_LOSS_PDF",
                            )}
                            downloadBalanceSheet={downloadBalanceSheet}
                            canExportTrialBalanceJson={currentUser.flags.getFlag(
                                "PARTNER__CAN_EXPORT_TRIAL_BALANCE_JSON",
                            )}
                        />
                    </Tab>


                    {currentUser.flags.getFlag('PARTNER__CAN_SEE_PNL_PER_PERIOD') && (
                        <Tab label={'Per maand'} icon={RiCalendarLine} slug="pnl-per-period">
                            <PerMonthView
                                currentUser={currentUser}
                                businessId={clientData.id}
                                legalEntityType={clientData.legal_entity_type}
                                statement={perMonthStatement}
                                onStatementChange={setPerMonthStatement}
                                canSeeBalanceSheet={currentUser.flags.getFlag('PARTNER__CAN_SEE_BALANCE_SHEET_PER_PERIOD')}
                                fetchProfitAndLossPerPeriod={fetchProfitAndLossPerPeriod}
                                fetchBalanceSheetPerPeriod={fetchBalanceSheetPerPeriod}
                                refreshTrialBalanceCache={refreshTrialBalanceCache}
                            />
                        </Tab>
                    )}

                    <Tab label={'Belastingen'} icon={RiGovernmentLine} slug="taxes">
                        <div className="p-4">
                            <h2 className="text-xl font-semibold mb-4">Belastinggeschiedenis</h2>
                            <TaxHistoryView payments={taxData}/>
                        </div>
                    </Tab>
                    <Tab label={'Bankrekeningen'} icon={RiBankLine} slug="bank-accounts">
                        <PartnerClientCashBalanceHistoryChart client={clientData} api={api}/>
                    </Tab>


                    <Tab label={'Cashflow'} icon={RiLineChartLine} slug="cashflow">
                        <CashflowStatement
                            business={{
                                ...clientData,
                                transaction_history_cutoff_date: {
                                    cutoff_date: clientData.transaction_history_cutoff_date
                                }
                            }}
                            api={api}
                            showBankAccountSelector={true}
                            isPartnerView={true}
                        />
                    </Tab>

                    {canMakeManualBookings && (
                        <Tab label={'Memoriaal'} icon={RiQuillPenLine} slug="journal">
                            <SubledgerProvider user={currentUser} clientBusinessId={clientData.id}>
                                <GeneralJournalBookingsTab
                                    businessId={clientData.id}
                                    glAccounts={glAccounts}
                                    onFetchBookings={fetchManualBookings}
                                    onSaveBooking={saveManualBooking}
                                    onUpdateBooking={editManualBooking}
                                    onDeleteBooking={deleteManualBooking}
                                    onFetchBankAccounts={fetchBankAccounts}
                                    onFetchBankBalance={fetchBankBalanceAtDate}
                                />
                            </SubledgerProvider>
                        </Tab>
                    )}

                    <Tab label={'Vaste Activa'} icon={RiBox3Line} slug="fixed-assets">
                        <FixedAssetsTab
                            currentUser={currentUser}
                            clientBusinessId={clientData.id}
                        />
                    </Tab>

                {currentUser.flags.getFlag('PARTNER__CAN_SEE_VAT_DECLARATION') && (
                    <Tab label={'BTW-Aangifte'} icon={RiFileTextLine} slug="vat">
                            <VatReturnTab
                                key={businessId}
                                businessId={businessId}
                                clientName={clientData.name}
                                vatReturns={vatReturns}
                                today={new Date()}
                                onSaveDraft={vatReturnActions.saveDraft}
                                onRequestApproval={vatReturnActions.requestApproval}
                                onWithdrawApproval={vatReturnActions.withdrawApproval}
                            />
                    </Tab>
                )}
            </Tabs>
            </div>

            <Modal open={isSettingsOpen} updateOpenState={setIsSettingsOpen} title="Instellingen">
                <section>
                    <h3 className="border-b border-gray-200 pb-2 text-lg font-semibold text-gray-900">
                        E-mailadres voor documenten
                    </h3>
                    <div className="mt-4 flex flex-col gap-2">
                        <label className="block text-sm font-semibold text-gray-900">
                            Aanleveradres
                        </label>
                        <p className="text-sm text-gray-500">
                            Dit is het mail adres dat {clientData?.name} heeft om documenten met je te delen.
                        </p>
                        <CopyToClipboard text={clientData?.documentCollectionEmail} className=""/>
                    </div>
                </section>

                <section className="mt-8">
                <ClientSettings
                    client={clientData}
                    onSave={async (settings) => {
                        if (settings.businessDescription !== (clientData.description ?? '')) {
                            await api.Partner__business__editDescription({
                                business_id: clientData.id,
                                description: settings.businessDescription
                            });
                        }

                        await api.Partner__business__editBookingSuggestions({
                            business_id: clientData.id,
                            booking_suggestions: settings.bookingSuggestions
                        });

                        const updatePromises = [
                            api.Partner__business__updateSetting({
                                business_id: clientData.id,
                                setting_name: "PARTNER_DOCUMENT_COLLECTION_EMAIL_SEPARATION_ENABLED",
                                setting_value: settings.emailSeparationEnabled
                            }),
                            api.Partner__business__updateSetting({
                                business_id: clientData.id,
                                setting_name: "PARTNER_DOCUMENT_COLLECTION_OTHER_DOCUMENTS_EMAIL_ADDRESS",
                                setting_value: settings.otherDocumentsEmailAddress ?? ''
                            }),
                            api.Partner__business__updateSetting({
                                business_id: clientData.id,
                                setting_name: "PARTNER_DOCUMENT_COLLECTION_SALES_INVOICE_EMAIL_ADDRESS",
                                setting_value: settings.salesInvoiceEmailAddress ?? ''
                            }),
                            api.Partner__business__updateSetting({
                                business_id: clientData.id,
                                setting_name: "PARTNER_DOCUMENT_COLLECTION_PURCHASE_INVOICE_EMAIL_ADDRESS",
                                setting_value: settings.purchaseInvoiceEmailAddress ?? ''
                            }),
                            api.Partner__business__updateSetting({
                                business_id: clientData.id,
                                setting_name: "ENTREPRENEUR_MISSING_DOCUMENTS_START_DATE",
                                setting_value: settings.missingDocumentsStartDate ?? ''
                            }),
                            api.Partner__business__updateSetting({
                                business_id: clientData.id,
                                setting_name: "BOOKKEEPING_START_DATE",
                                setting_value: settings.bookkeepingStartDate ?? ''
                            }),
                            api.Partner__business__updateSetting({
                                business_id: clientData.id,
                                setting_name: ENTREPRENEUR_CAN_SEE_REPORTS_SETTING,
                                setting_value: settings.entrepreneurCanSeeReports
                            })
                        ];

                        await Promise.all(updatePromises);

                        const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
                        const updatedClients = await api.Partner__myClients(new Date().getFullYear(), currentQuarter);
                        const updatedClient = updatedClients.businesses.find(b => b.id === businessId);
                        const updatedDocCollection = await api.Partner__entrepreneur__documentCollectionEmail(businessId!);

                        if (updatedClient) {
                            setClientData({
                                ...updatedClient,
                                documentCollectionEmail: updatedDocCollection.email
                            } as ClientData);
                        }
                    }}
                    getAllSettingsForBusiness={getAllSettingsForBusiness}
                />
                </section>

                {canUseSubledgers && (
                    <section className="mt-8">
                        <SubledgersSection
                            subledgers={subledgers}
                            isLoading={isLoadingSubledgers}
                            onCreate={createSubledger}
                            onRename={renameSubledger}
                        />
                    </section>
                )}
            </Modal>

            {canImportBksDocuments && (
                <NarrowDrawer open={isImportOpen} updateOpenState={setIsImportOpen} title="Importeren">
                    <DocumentImportSection {...bksImport} hideHeader/>
                </NarrowDrawer>
            )}

            {canSeePayroll && isPayrollOpen && (
                <NarrowDrawer open={isPayrollOpen} updateOpenState={setIsPayrollOpen} title="Loonadministratie">
                    <ClientPayrollSection api={api} user={currentUser} businessId={clientData.id} providerName={payrollProviderName}/>
                </NarrowDrawer>
            )}

            {canInviteEntrepreneurs && isAccessOpen && (
                <BusinessAccessModal
                    currentUser={currentUser}
                    businessId={businessId!}
                    open={isAccessOpen}
                    updateOpenState={setIsAccessOpen}
                    onAccessChanged={reloadAccessCount}
                />
            )}
        </Shell>
    );
}
